import { useCallback, useState, useEffect } from "react";

/** Components */
import { ButtonGroup, Button, Collapse, TableContainer, Paper, Table, TableHead, TableCell, TableRow, TableBody, Tooltip } from "@mui/material";
import { FilterBy } from "../../commons/filter.component";
import ButtonWithAPICall from "../../commons/ButtonWithAPICall.component";
import CustomDatePicker from "../../commons/CustomDatePicker";
import { useSnackbar } from "notistack";
import LoadingProgress from "../../commons/LoadingProgress.component";

/** Icons */
import SearchIcon from "@mui/icons-material/Search";
import { ExcelIcon } from "../../commons/miscellaneous";

/** Api */
import { getCustomers, getCurrency, getRptExpCI } from "../../../services/api";
import { setHeaderTitle } from "../../../store/layoutSlide";

/** Package */
import dayjs from "dayjs";

/** Hook */
import { useDispatch } from "react-redux";
import { moneyFormat } from "../../config";

/** Init Filter */
const initialFilter = {
  start_date: "",
  end_date: "",
  selectedCustomer: null,
  currency: null,
};

const RptExpCI = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  /** Init state */
  const [open, setOpen] = useState(true);
  const [permit, setPermit] = useState(true);
  const [input, setInput] = useState({ ...initialFilter, end_date: dayjs().format("YYYY-MM-DD") });
  const [data, setData] = useState([]);
  /** Function */

  const handleSearch = useCallback(async () => {
    setData();
    const params = {};
    if (input.selectedCustomer) params.customer_id = input.selectedCustomer.id;
    if (input.start_date) params.start_date = input.start_date;
    if (input.end_date) params.end_date = input.end_date;
    if (input.currency) params.currency = input.currency.code;
    return getRptExpCI(params).then((res) => {
      if (res.code === 403) {
        setPermit(false);
        setData([]);
        return;
      }
      if (res.code !== 200) {
        enqueueSnackbar(res.message);
        setData([]);
        return;
      }
      setData(res.data);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  useEffect(() => {
    dispatch(setHeaderTitle("BÁO CÁO THEO DÕI LƯỢNG XUẤT CI/PN"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div align="right" style={{ marginBottom: 10, marginTop: 10, backgroundColor: "#b3deff", height: 40 }}>
        <ButtonGroup style={{ height: 40, marginRight: 10 }}>
          <Button>
            <Tooltip title="Xuất dữ liệu ra file Excel">
              <ExcelIcon />
            </Tooltip>
          </Button>
          <Button onClick={() => setOpen((prev) => !prev)} variant="outlined">
            <Tooltip title="Thư mục">
              <SearchIcon />
            </Tooltip>
          </Button>
        </ButtonGroup>
      </div>
      {/* Filter Group */}
      <Collapse in={open}>
        <FilterBy
          style={{ display: "inline-flex", margin: "5px", minWidth: 240 }}
          getDataFunc={() => getCustomers({ status: 1 })}
          value={input.selectedCustomer || null}
          onChange={(e, newValue) => setInput((prev) => ({ ...prev, selectedCustomer: newValue }))}
          label="Khách hàng"
        />
        <CustomDatePicker label="Ngày bắt đầu" value={input.start_date} onChange={(value) => setInput((prev) => ({ ...prev, start_date: value }))} sx={{ margin: "5px" }} />
        <CustomDatePicker label="Ngày kết thúc" value={input.end_date} onChange={(value) => setInput((prev) => ({ ...prev, end_date: value }))} sx={{ margin: "5px" }} />
        <FilterBy
          style={{ display: "inline-flex", margin: "5px", minWidth: 240 }}
          getDataFunc={() => getCurrency({ status: 1 })}
          value={input.currency || null}
          onChange={(e, newValue) => setInput((prev) => ({ ...prev, currency: newValue }))}
          label="Tiền tệ"
          getOptionLabel={(option) => option.code}
        />
        <ButtonWithAPICall api={handleSearch} disableSnackbar={() => true} variant="outlined">
          Tìm kiếm
        </ButtonWithAPICall>
      </Collapse>
      {!data ? (
        <LoadingProgress />
      ) : (
        <TableContainer component={Paper} style={{ width: "98%", marginLeft: 10 }} sx={{ maxHeight: 800 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2} style={{ border: "1px solid lightgray" }} colSpan={1}>
                  STT
                </TableCell>
                <TableCell rowSpan={2} style={{ border: "1px solid lightgray" }} colSpan={1}>
                  Khách hàng
                </TableCell>
                <TableCell rowSpan={2} style={{ border: "1px solid lightgray" }} colSpan={1}>
                  Hãng bay
                </TableCell>
                <TableCell rowSpan={2} style={{ border: "1px solid lightgray" }} colSpan={1}>
                  Chi nhánh/Kho hàng
                </TableCell>
                <TableCell rowSpan={2} style={{ border: "1px solid lightgray" }} colSpan={1}>
                  Đơn vị tính
                </TableCell>
                <TableCell rowSpan={1} style={{ border: "1px solid lightgray" }} colSpan={3}>
                  Lượng hàng xuất
                </TableCell>
                <TableCell rowSpan={1} style={{ border: "1px solid lightgray" }} colSpan={3}>
                  Lượng hàng đã ghi nhận CI.PN
                </TableCell>
                <TableCell rowSpan={1} style={{ border: "1px solid lightgray" }} colSpan={3}>
                  Lượng hàng chưa ghi nhận CI.PN
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ border: "1px solid lightgray" }}>SL</TableCell>
                <TableCell style={{ border: "1px solid lightgray" }}>Kg</TableCell>
                <TableCell style={{ border: "1px solid lightgray" }}>Tổng tiền</TableCell>
                <TableCell style={{ border: "1px solid lightgray" }}>SL</TableCell>
                <TableCell style={{ border: "1px solid lightgray" }}>Kg</TableCell>
                <TableCell style={{ border: "1px solid lightgray" }}>Tổng tiền</TableCell>
                <TableCell style={{ border: "1px solid lightgray" }}>SL</TableCell>
                <TableCell style={{ border: "1px solid lightgray" }}>Kg</TableCell>
                <TableCell style={{ border: "1px solid lightgray" }}>Tổng tiền</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {data?.length ? (
                data.map((row, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell component="div" style={{ border: "1px solid lightgray" }}>
                        {index + 1}
                      </TableCell>
                      <TableCell component="div" style={{ border: "1px solid lightgray" }} align="left">
                        {index >= 1 && data[index - 1].customer_id === row.customer_id ? "" : row.customer_name}
                      </TableCell>
                      <TableCell component="div" style={{ border: "1px solid lightgray" }} align="left">
                        {row.airline}
                      </TableCell>
                      <TableCell component="div" style={{ border: "1px solid lightgray" }} align="left">
                        {row.inventory_name}
                      </TableCell>
                      <TableCell component="div" style={{ border: "1px solid lightgray" }}>
                        {row.product_unit}
                      </TableCell>
                      <TableCell component="div" style={{ border: "1px solid lightgray" }} align="right">
                        {moneyFormat(row.quantity, "VND")}
                      </TableCell>
                      <TableCell component="div" style={{ border: "1px solid lightgray" }} align="right">
                        {moneyFormat(row.kg, "VND")}
                      </TableCell>
                      <TableCell component="div" style={{ border: "1px solid lightgray" }} align="right">
                        {moneyFormat(row.total, input.currency.code)}
                      </TableCell>
                      <TableCell component="div" style={{ border: "1px solid lightgray" }} align="right">
                        {moneyFormat(row.sold_quantity, "VND")}
                      </TableCell>
                      <TableCell component="div" style={{ border: "1px solid lightgray" }} align="right">
                        {moneyFormat(row.sold_kg, "VND")}
                      </TableCell>
                      <TableCell component="div" style={{ border: "1px solid lightgray" }} align="right">
                        {moneyFormat(row.sold_total, input.currency.code)}
                      </TableCell>
                      <TableCell component="div" style={{ border: "1px solid lightgray" }} align="right">
                        {moneyFormat(row.quantity - row.sold_quantity, "VND")}
                      </TableCell>
                      <TableCell component="div" style={{ border: "1px solid lightgray" }} align="right">
                        {moneyFormat(row.kg - row.sold_kg, "VND")}
                      </TableCell>
                      <TableCell component="div" style={{ border: "1px solid lightgray" }} align="right">
                        {moneyFormat(row.total - row.sold_total, input.currency.code)}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={20}>
                    {permit ? "Không có dữ liệu" : "No permission"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};
export default RptExpCI;
